import {
  Alert,
  Button,
  Checkbox,
  Icons,
  InputNumber,
  Modal,
  Select,
  Space,
  Typography,
} from '@pankod/refine-antd';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { useEffect, useMemo, useState } from 'react';
import { getAllCompanyConfig } from 'services/config';
import { IWarehouseDropDown, getWarehousesDropdown } from 'services/warehouse';
import {
  BarcodeStickerOptions,
  barcodePayloadForSku,
  barcodeStickerPriceText,
  buildStickerDetailTexts,
  buildStickerSheetHtml,
  readBarcodeStickerOptions,
  renderBarcodeSvgMarkup,
  writeBarcodeStickerOptions,
} from 'utilities/barcode-sticker';
import { printPosInvoice } from 'utilities/print';
import {
  StickerPaperPresetKey,
  defaultStickerCopies,
  fitStickerContent,
  labelsPerSheet,
  maximumStickerCopies,
  stickerDetailPartNames,
  stickerPaperPresetList,
  stickerPaperPresets,
} from 'utilities/sticker-paper';

interface BarcodeStickerButtonProps {
  variant: { productVariantName: string; sku: string; sellingPrice: number };
  // The organization's VAT percent, 0 when VAT is not set or `sale.vatRate` is off. Passed in by
  // the product list so each row does not fetch it again.
  vatRatePercent?: number;
}

const pixelsPerMillimetre = 96 / 25.4;
// The preview fits a sheet into the dialog; a single roll label is shown doubled.
const previewWidthPixels = 560;

// localStorage can throw in a private window or with site data blocked.
const browserStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

// Stitches S8, flag product.barcodeSticker: Code 128 stickers for one variant, on a label roll or a
// sheet of labels, printable from the product list.
export const BarcodeStickerButton: React.FC<BarcodeStickerButtonProps> = ({
  variant,
  vatRatePercent = 0,
}) => {
  const isEnabled = useFeatureFlag('product.barcodeSticker');
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [options, setOptions] = useState<BarcodeStickerOptions>(() =>
    readBarcodeStickerOptions(browserStorage()),
  );
  const preset = stickerPaperPresets[options.paperPresetKey];
  const [copies, setCopies] = useState(() => defaultStickerCopies(preset));
  const [startPosition, setStartPosition] = useState(1);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);

  const updateOptions = (changes: Partial<BarcodeStickerOptions>) => {
    const nextOptions = { ...options, ...changes };
    setOptions(nextOptions);
    writeBarcodeStickerOptions(browserStorage(), nextOptions);
  };

  // Company and showrooms are fetched once, when the dialog first opens.
  useEffect(() => {
    if (!isPreviewVisible || companyName !== null) return;
    let isCancelled = false;
    getAllCompanyConfig()
      .then((companyConfigs) => {
        if (!isCancelled) setCompanyName(companyConfigs?.[0]?.companyName ?? '');
      })
      .catch(() => {
        if (!isCancelled) setCompanyName('');
      });
    getWarehousesDropdown()
      .then((warehouseList) => {
        if (!isCancelled) setWarehouses(warehouseList ?? []);
      })
      .catch(() => undefined);
    return () => {
      isCancelled = true;
    };
  }, [isPreviewVisible, companyName]);

  const payload = useMemo(() => barcodePayloadForSku(variant.sku), [variant.sku]);
  const barcodeSvgMarkup = useMemo(
    () => (isPreviewVisible && payload.isBarcodable ? renderBarcodeSvgMarkup(payload.value) : ''),
    [isPreviewVisible, payload],
  );

  if (!isEnabled) return null;

  const showroomWarehouse =
    warehouses.find((warehouse) => warehouse.id === options.showroomWarehouseId) ?? warehouses[0];
  const detailTexts = buildStickerDetailTexts({
    includeCompanyName: options.includeCompanyName,
    companyName,
    includeShowroomName: options.includeShowroomName,
    showroomName: showroomWarehouse?.name,
    includePrintDate: options.includePrintDate,
    printDate: new Date(),
  });
  const layout = fitStickerContent({
    labelWidth: preset.labelWidth,
    labelHeight: preset.labelHeight,
    safeArea: preset.safeArea,
    detailTexts,
  });
  const content = {
    productName: variant.productVariantName,
    sku: variant.sku,
    priceText: barcodeStickerPriceText({
      sellingPrice: variant.sellingPrice,
      vatRatePercent,
      includeVat: options.includeVat,
    }),
    barcodeSvgMarkup,
    detailTexts,
  };
  const perSheet = labelsPerSheet(preset);
  const isRoll = preset.kind === 'roll';
  const firstPageCopies = isRoll ? 1 : Math.min(copies, perSheet - startPosition + 1);
  const previewHtml = buildStickerSheetHtml({
    preset,
    content,
    copies: firstPageCopies,
    startPosition,
    printCutLines: options.printCutLines,
  });
  const pageWidthPixels = preset.pageWidth * pixelsPerMillimetre;
  const pageHeightPixels = preset.pageHeight * pixelsPerMillimetre;
  const previewScale = isRoll ? 2 : previewWidthPixels / pageWidthPixels;
  const sheetCount = isRoll ? copies : Math.ceil((startPosition - 1 + copies) / perSheet);
  const guideLegend =
    preset.kind === 'cutSheet'
      ? 'Trim (dashed) · Safe area (dotted) · Crop marks and cut lines (printed)'
      : 'Trim (dashed) · Safe area (dotted)';

  const choosePaper = (paperPresetKey: StickerPaperPresetKey) => {
    updateOptions({ paperPresetKey });
    setCopies(defaultStickerCopies(stickerPaperPresets[paperPresetKey]));
    setStartPosition(1);
  };

  const printStickers = () =>
    printPosInvoice(
      buildStickerSheetHtml({
        preset,
        content,
        copies,
        startPosition,
        printCutLines: options.printCutLines,
      }),
    );

  return (
    <>
      <Button
        size="small"
        title="Barcode sticker"
        icon={<Icons.BarcodeOutlined />}
        onClick={() => setIsPreviewVisible(true)}
      />
      <Modal
        visible={isPreviewVisible}
        width={isRoll ? 520 : 680}
        title={`Barcode stickers: ${preset.label}`}
        onCancel={() => setIsPreviewVisible(false)}
        footer={
          <Button
            type="primary"
            icon={<Icons.PrinterOutlined />}
            disabled={!payload.isBarcodable}
            onClick={printStickers}
          >
            {copies === 1 ? 'Print sticker' : `Print ${copies} stickers`}
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            aria-label="Paper"
            style={{ width: '100%' }}
            value={options.paperPresetKey}
            onChange={(value: StickerPaperPresetKey) => choosePaper(value)}
            options={stickerPaperPresetList.map((paper) => ({
              value: paper.key,
              label: paper.label,
            }))}
          />
          <Space wrap>
            <span>
              Copies{' '}
              <InputNumber
                aria-label="Copies"
                min={1}
                max={maximumStickerCopies}
                value={copies}
                onChange={(value) =>
                  setCopies(Math.min(maximumStickerCopies, Math.max(1, Number(value) || 1)))
                }
              />
            </span>
            {!isRoll && (
              <span>
                Start at position{' '}
                <InputNumber
                  aria-label="Start at position"
                  min={1}
                  max={perSheet}
                  value={startPosition}
                  onChange={(value) =>
                    setStartPosition(Math.min(perSheet, Math.max(1, Number(value) || 1)))
                  }
                />{' '}
                of {perSheet}
              </span>
            )}
          </Space>
          <Space wrap>
            {vatRatePercent > 0 && (
              <Checkbox
                checked={options.includeVat}
                onChange={(event) => updateOptions({ includeVat: event.target.checked })}
              >
                Show price with VAT ({vatRatePercent}%)
              </Checkbox>
            )}
            <Checkbox
              checked={options.includeCompanyName}
              onChange={(event) => updateOptions({ includeCompanyName: event.target.checked })}
            >
              Company name
            </Checkbox>
            <Checkbox
              checked={options.includeShowroomName}
              onChange={(event) => updateOptions({ includeShowroomName: event.target.checked })}
            >
              Showroom
            </Checkbox>
            {options.includeShowroomName && (
              <Select
                aria-label="Showroom"
                style={{ minWidth: 160 }}
                value={showroomWarehouse?.id}
                onChange={(value: string) => updateOptions({ showroomWarehouseId: value })}
                options={warehouses.map((warehouse) => ({
                  value: warehouse.id,
                  label: warehouse.name,
                }))}
              />
            )}
            <Checkbox
              checked={options.includePrintDate}
              onChange={(event) => updateOptions({ includePrintDate: event.target.checked })}
            >
              Print date
            </Checkbox>
            {preset.kind === 'cutSheet' && (
              <Checkbox
                checked={options.printCutLines}
                onChange={(event) => updateOptions({ printCutLines: event.target.checked })}
              >
                Print cut lines
              </Checkbox>
            )}
          </Space>
          {layout.droppedParts.map((part) => (
            <Alert
              key={part}
              type="info"
              showIcon
              message={`${stickerDetailPartNames[part]} doesn't fit on this label`}
            />
          ))}
          {payload.isBarcodable ? (
            <>
              <div
                style={{
                  width: pageWidthPixels * previewScale,
                  height: pageHeightPixels * previewScale,
                  margin: '0 auto',
                  overflow: 'hidden',
                  border: '1px solid #f0f0f0',
                }}
              >
                <iframe
                  title="Sticker preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  style={{
                    width: pageWidthPixels,
                    height: pageHeightPixels,
                    border: 0,
                    transform: `scale(${previewScale})`,
                    transformOrigin: 'top left',
                  }}
                />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {guideLegend}
                {!isRoll && ` · Page 1 of ${sheetCount}`}
              </Typography.Text>
              <Typography.Text strong style={{ fontSize: 12 }}>
                Print at 100% / Actual size, no fit-to-page.
              </Typography.Text>
            </>
          ) : (
            <Alert type="warning" showIcon message={payload.message} />
          )}
        </Space>
      </Modal>
    </>
  );
};
